<template>
	<BaseLayout
		:footer-full="true"
		:include-padding="false"
		background-color-class="bg-color-white"
		:content-full-height="true"
	>
		<BasePlaceHolder :class="{ 'container pt-100': isLoading }" :line-count="8" :placehold="isLoading">
			<header class="bg-color-greige">
				<div class="container">
					<div class="col-8-md col-offset-2-md pt-250 pb-100">
						<div class="col-7-md">
							<Tag
								v-if="assetStatus"
								:text="assetStatus"
								:background="statusBackground"
								data-test="asset-status"
							/>
							<h1 v-if="assetName" class="mt-50 heading-1" data-test="asset-name">{{ assetName }}</h1>
							<div v-if="assetLocation" class="text-color-content-secondary mt-25">{{
								assetLocation
							}}</div>
						</div>
					</div>
				</div>
			</header>
			<JumpLinkNav
				classes="col-8-md col-offset-2-md"
				:jump-links="jumpLinks"
				:set-border="false"
				:root-margin="200"
				background-color="greige"
				mixpanel-context="Asset page hero"
				data-test="asset-details-real-estate-jump-links"
				class="mb-600"
			>
				<div v-if="asset" id="asset-overview" class="container pt-150 pt-250-md" data-context="asset-overview">
					<AssetDetailsOverview class="col-8-md col-offset-2-md" :asset="asset" />
				</div>
				<div
					v-if="showUpdates"
					id="asset-updates"
					class="container pt-150 pt-250-md"
					data-context="asset-updates"
				>
					<AssetDetailsUpdatesTimeline class="col-8-md col-offset-2-md" :updates="updates" />
				</div>
				<div
					v-if="asset && showLocation"
					id="location"
					class="container pt-150 pt-250-md"
					data-context="location"
					data-test="asset-details-location"
				>
					<AssetDetailsMarketInfo class="col-8-md col-offset-2-md" :asset="asset"
				/></div>
				<div
					v-if="assetType && showAssetType"
					id="asset-type"
					class="container pt-150 pt-250-md"
					data-context="asset-type"
				>
					<AssetDetailsAssetType class="col-8-md col-offset-2-md" :data="assetType" />
				</div>
				<div
					v-if="strategy && showStrategy"
					id="our-strategy"
					class="container pt-150 pt-250-md"
					data-context="our-strategy"
					data-test="asset-details-strategy"
				>
					<AssetDetailsOurStrategy class="col-8-md col-offset-2-md" :data="strategy" />
				</div>
				<div
					v-if="showFunds"
					id="fund-information"
					class="container pt-150 pt-250-md"
					data-context="fund-information"
					data-test="asset-details-real-estate-funds"
				>
					<div class="col-8-md col-offset-2-md">
						<h4 class="heading-5">{{ fundInfoSectionTitle }}</h4>
						<div class="display-flex flex-direction-column gap-100 mt-100">
							<AssetDetailsAssociatedFund v-for="data in funds" :key="data.fund.name" :data="data" />
						</div>
					</div>
				</div>
			</JumpLinkNav>
		</BasePlaceHolder>
		<template #footer>
			<BasePlaceHolder :line-count="3" :placehold="isLoading" :class="{ container: isLoading }">
				<FooterFull :disclaimers="disclaimers" />
			</BasePlaceHolder>
		</template>
	</BaseLayout>
</template>

<script setup lang="ts">
import { Asset, AssetFundAllocation, AssetStrategy, AssetType, AssetUpdateSanity } from 'types/assets';
import { computed, defineAsyncComponent, ref } from 'vue';
import { JumpLink, TagBackground } from 'types/layout';
import { useRoute, useRouter } from 'vue-router';
import AssetDetailsOverview from '@components/marketing-site/asset-details/asset-details-overview.vue';

// Below-the-fold components loaded asynchronously for better LCP
const AssetDetailsAssetType = defineAsyncComponent(
	() => import('@components/marketing-site/asset-details/asset-details-asset-type.vue')
);
const AssetDetailsAssociatedFund = defineAsyncComponent(
	() => import('@components/marketing-site/asset-details/asset-details-associated-fund.vue')
);
const AssetDetailsMarketInfo = defineAsyncComponent(
	() => import('@components/marketing-site/asset-details/asset-details-market-info.vue')
);
const AssetDetailsOurStrategy = defineAsyncComponent(
	() => import('@components/marketing-site/asset-details/asset-details-our-strategy.vue')
);
const AssetDetailsUpdatesTimeline = defineAsyncComponent(
	() => import('@components/marketing-site/asset-details/asset-details-updates-timeline.vue')
);
import { AssetQueryParams } from 'types/sanity/proxy';
import FooterFull from '@components/footer/footer-full.vue';
import { getAsset } from '@api/sanity/proxy/assets';
import JumpLinkNav from '@components/layout/jump-link-nav.vue';
import { MixpanelContext } from 'types/analytics';
import { pluralize } from '@filters/shared-filters';
import Tag from '@components/tags/tag-component.vue';
import { useAnalyticsProperties } from '@utils/composables/use-analytics-properties';
import { useHead } from '@unhead/vue';
import { useMixpanel } from '@utils/composables/use-mixpanel';

const route = useRoute();
const router = useRouter();
const { setViewProperties, setCustomProperties } = useAnalyticsProperties();

const isRealEstateAsset = computed((): boolean => 'reitElementId' in route.params);

const isLoading = ref(true);

const asset = ref<Asset | null>(null);

const assetStatus = computed((): string => asset.value?.status.label ?? '');

const isAssetActive = computed((): boolean => asset.value?.status.name === 'ACTIVE');

const isAssetCompleted = computed((): boolean => asset.value?.status.name === 'INACTIVE');

const statusBackground = computed(
	(): TagBackground => (isAssetActive.value || isAssetCompleted.value ? 'green' : 'default')
);

const assetName = computed((): string => asset.value?.name ?? '');

const assetLocation = computed((): string => {
	const city = asset.value?.realEstateAndPrivateCreditFields?.city;
	const state = asset.value?.realEstateAndPrivateCreditFields?.state;

	if (city && state) {
		return `${city}, ${state}`;
	} else {
		return '';
	}
});

const updates = computed((): Array<AssetUpdateSanity> => {
	return asset.value?.associatedUpdates ?? [];
});

const assetType = computed((): AssetType | null => {
	return asset.value?.assetType ?? null;
});

const strategy = computed((): AssetStrategy | null => {
	return asset.value?.realEstateAndPrivateCreditFields?.strategy ?? null;
});

const funds = computed((): Array<AssetFundAllocation> => asset.value?.fundAllocations ?? []);
const fundInfoSectionTitle = computed((): string => `Associated ${pluralize(funds.value.length, 'fund')}`);

const showUpdates = computed((): boolean => !!updates.value.length);

const showLocation = computed(
	(): boolean =>
		!!asset.value?.realEstateAndPrivateCreditFields?.latitude &&
		!!asset.value?.realEstateAndPrivateCreditFields?.longitude &&
		isRealEstateAsset.value
);

const showAssetType = computed((): boolean => !!assetType.value?.description);

const showFunds = computed(() => !!funds.value.length);

const showStrategy = computed((): boolean => !!strategy.value && isRealEstateAsset.value);

const jumpLinks = computed((): Array<JumpLink> => {
	const jumpLinks = [];

	jumpLinks.push({
		key: 'asset-overview',
		label: 'Overview'
	});

	if (showUpdates.value) {
		jumpLinks.push({
			key: 'asset-updates',
			label: 'Updates'
		});
	}

	if (showLocation.value) {
		jumpLinks.push({
			key: 'location',
			label: 'Location'
		});
	}

	if (showAssetType.value) {
		jumpLinks.push({
			key: 'asset-type',
			label: 'Asset type'
		});
	}

	if (showStrategy.value) {
		jumpLinks.push({
			key: 'our-strategy',
			label: 'Strategy'
		});
	}

	if (showFunds.value) {
		jumpLinks.push({
			key: 'fund-information',
			label: 'Funds'
		});
	}

	return jumpLinks;
});

const viewName = ref('Asset Detail');
const viewTitle = computed(() => asset.value?.name ?? asset.value?.assetType.name);
const customProperties = computed(
	(): MixpanelContext => ({
		'Asset Class': funds.value[0]?.fund?.assetClass.enumValue ?? ''
	})
);

const disclaimers = computed(() => {
	return asset.value?.footerDisclaimer ?? [];
});

setViewProperties({ viewName, viewTitle });
setCustomProperties(customProperties);

async function setup(): Promise<void> {
	try {
		if (isRealEstateAsset.value) {
			const assetQueryParams: AssetQueryParams = {
				elementType: 'REAL_ESTATE',
				include: ['INVESTOR_UPDATES']
			};
			asset.value = await getAsset(route.params.reitElementId as string, assetQueryParams);
		} else {
			const assetQueryParams: AssetQueryParams = {
				elementType: 'BOND',
				include: ['INVESTOR_UPDATES']
			};
			asset.value = await getAsset(route.params.bondId as string, assetQueryParams);
		}

		if (!asset.value) {
			await router.push({ name: '404' });
		}

		trackMixpanelView('Page', {
			'View Name': viewName.value,
			'View Title': viewTitle.value,
			customProperties: customProperties.value
		});

		useHead({
			title: assetName.value,
			meta: [{ property: 'og:title', content: assetName.value }]
		});

		isLoading.value = false;
	} catch {
		await router.push({ name: '404' });
	}
}

setup();
const { trackMixpanelView } = useMixpanel();
</script>
